import { useCollection, useDocument } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { db } from "@/firebase";
import { formatCurrency } from "@/utils/tools.js";
import type { Transaction } from "@/utils/types.js";
import type { Account } from "@aircap/common/account";
import type { GclAccount, GclTransaction } from "@aircap/common/gcl";
import { getGclTransactionName } from "@aircap/common/gcl";
import { collection, doc, orderBy, query, updateDoc } from "firebase/firestore";
import { computed, type Ref } from "vue";
import { type VueFirestoreDocumentData } from "vuefire";

export const useGclTransactions = (
  account: Ref<VueFirestoreDocumentData<Account> | undefined>,
  filters?: {
    beneficiaryIban?: Ref<string | undefined>;
    isFuture?: Ref<boolean>;
  },
) => {
  const { t: $t, locale } = useI18n();

  const bankAccountRef = computed(() => {
    return account.value?.bankAccountId
      ? doc<GclAccount>(
          db,
          `accounts/${account.value?.id}/gclAccounts/${account.value?.bankAccountId}`,
        )
      : null;
  });

  const { data: bankAccount } = useDocument<GclAccount>(bankAccountRef);

  const transactionsSource = computed(() => {
    if (!account.value?.bankAccountId) return null;

    if (filters?.beneficiaryIban?.value)
      throw new Error("Beneficiary Iban filter is not compatible with GCL transactions");
    if (filters?.isFuture?.value)
      throw new Error("isFuture filter is not compatible with GCL transactions");

    return query(
      collection<GclTransaction>(
        db,
        `accounts/${account.value?.id}/gclAccounts/${account.value?.bankAccountId}/transactions`,
      ),
      orderBy<GclTransaction>("bookingDate", "desc"),
    );
  });

  const { data: transactionCollection, pending: loading } =
    useCollection<GclTransaction>(transactionsSource);

  const transactions = computed<Transaction[]>(() =>
    transactionCollection.value
      .filter((t): t is typeof t & { bookingDate: string } => !!t.bookingDate)
      .map(
        (t) =>
          ({
            date: t.bookingDate,
            id: t.id,
            name: getGclTransactionName(t, bankAccount.value),
            amount: parseFloat(t.transactionAmount.amount),
            category: t.categoryId
              ? { id: t.categoryId, name: $t(`categories.${t.categoryId}`) }
              : undefined,
            trackedCategorySplits: t.trackedCategorySplits,
            suggestedTrackedCategoryId: t.suggestedTrackedCategoryId,
            rejectedSuggestedTrackedCategoryId: t.rejectedSuggestedTrackedCategoryId,
            currencyCode: t.transactionAmount.currency,
            amountFormatted: formatCurrency({
              value: parseFloat(t.transactionAmount.amount),
              currencyCode: t.transactionAmount.currency,
              sign: true,
              locale: locale.value,
            }),
            is_future: false,
            is_flagged: t.is_flagged,
            dateFormatted: {
              short: new Intl.DateTimeFormat(locale.value).format(new Date(t.bookingDate)),
              long: new Intl.DateTimeFormat(locale.value, {
                dateStyle: "long",
                timeStyle: "short",
              }).format(new Date(t.bookingDate)),
            },
          }) satisfies Transaction,
      ),
  );

  const flagTransaction = (transactionId: string, value: boolean) => {
    if (!account.value?.id || !account.value.bankAccountId) return;
    const transactionRef = doc<GclTransaction>(
      db,
      `accounts/${account.value.id}/gclAccounts/${account.value.bankAccountId}/transactions`,
      transactionId,
    );

    void updateDoc(transactionRef, { is_flagged: value });
  };

  return {
    transactions,
    loading,
    flagTransaction,
  };
};
